const SDK_URL = 'https://checkout.placetopay.com/lightbox.min.js';
const SDK_ID = 'evertec-sdk';
const SDK_LOAD_TIMEOUT = 15000;

const Evertec = () => {
  let evertecInstance;
  let scriptPromise = null;
  let redirectListenerAttached = false;
  let lightboxListenerInstance = null;
  let lightboxCallbacks = null;

  const createSession = async ({
    purchaseUrl,
    brand,
    purchaseId,
    description,
    currency,
    amount,
    returnUrl,
    cancelUrl,
    paymentType,
    purchaser,
  }) => {
    try {
      const purchaserPhone = purchaser.phoneCode
        ? `+${purchaser.phoneCode}${purchaser.phone}`
        : purchaser.phone;
      const resp = await fetch(`${purchaseUrl}/v2/payment_session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_engine: 'evertec',
          tenant: brand,
          purchase_id: purchaseId,
          description,
          currency,
          amount,
          return_url: returnUrl,
          cancel_url: cancelUrl,
          payment_type: paymentType,
          skip_result: true,
          document: purchaser.documentId,
          document_type: purchaser.documentType,
          name: purchaser.purchaserFirstName,
          surname: purchaser.purchaserLastName,
          email: purchaser.email,
          mobile: purchaserPhone,
        }),
      });
      const data = await resp.json().catch(() => null);
      if (!resp.ok || !data || !data.process_url) {
        return false;
      }
      return data;
    } catch (error) {
      console.error('Error getting payment URL:', error);
      return false;
    }
  };

  /**
   * Redirect the parent window when the Lightbox cannot keep its first-party
   * session (e.g. Safari/iOS blocking third-party cookies, or a payment method
   * that requires a full redirect). Required by Evertec's Lightbox certification.
   */
  const handleRedirectMessage = (event) => {
    let { data } = event;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }
    if (data && data.type === 'placetopay-lightbox:redirect' && data.url) {
      window.location.href = data.url;
    }
  };

  const attachRedirectListener = () => {
    if (redirectListenerAttached) return;
    window.addEventListener('message', handleRedirectMessage, false);
    redirectListenerAttached = true;
  };

  const clearLightboxCallbacks = () => {
    lightboxCallbacks = null;
  };

  const handleLightboxResponse = (response) => {
    const callbacks = lightboxCallbacks;
    clearLightboxCallbacks();

    if (!callbacks) return;

    const { status } = response?.status || {};
    if (status === 'REJECTED') {
      callbacks.onRejected?.(response);
      return;
    }

    callbacks.onApproved?.(response);
  };

  const handleLightboxClose = () => {
    const callbacks = lightboxCallbacks;
    clearLightboxCallbacks();

    callbacks?.onClose?.();
  };

  const attachLightboxListeners = (instance) => {
    if (!instance || lightboxListenerInstance === instance) return;

    instance.on('response', handleLightboxResponse);
    instance.on('close', handleLightboxClose);
    lightboxListenerInstance = instance;
  };

  /**
   * Inject the Lightbox SDK once, reusing an existing instance/tag on retries
   * and rejecting (instead of hanging) when the script fails to load.
   */
  const loadScript = () => {
    if (window.P) return Promise.resolve(window.P);
    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise((resolve, reject) => {
      let script = document.getElementById(SDK_ID);
      const existing = Boolean(script);

      let timeoutId;
      let onLoad;
      let onError;

      const cleanup = () => {
        clearTimeout(timeoutId);
        if (script) {
          script.removeEventListener('load', onLoad);
          script.removeEventListener('error', onError);
        }
      };

      const settleWhenLoaded = () => {
        if (window.P) {
          resolve(window.P);
        } else {
          scriptPromise = null;
          reject(new Error('Evertec lightbox loaded but window.P is undefined'));
        }
      };

      onLoad = () => {
        cleanup();
        settleWhenLoaded();
      };

      onError = () => {
        cleanup();
        scriptPromise = null;
        if (script) script.remove();
        reject(new Error('Failed to load Evertec lightbox script'));
      };

      // An externally injected tag may have already fired its load event before
      // we could attach a listener; resolve/reject from its state instead of
      // hanging on a load event that will never come again.
      if (existing && (script.readyState === 'loaded' || script.readyState === 'complete')) {
        settleWhenLoaded();
        return;
      }

      if (!script) {
        script = document.createElement('script');
        script.src = SDK_URL;
        script.id = SDK_ID;
        document.head.appendChild(script);
      }
      script.addEventListener('load', onLoad);
      script.addEventListener('error', onError);

      // Safety net for the case above when readyState is not exposed: reject
      // instead of leaving the promise (and its listeners) pending forever.
      timeoutId = setTimeout(() => {
        cleanup();
        scriptPromise = null;
        reject(new Error('Timed out loading Evertec lightbox script'));
      }, SDK_LOAD_TIMEOUT);
    });

    return scriptPromise;
  };

  const initialize = async (processUrl) => {
    if (!processUrl) {
      throw new Error('Missing processUrl for Evertec lightbox');
    }
    attachRedirectListener();
    clearLightboxCallbacks();
    const P = await loadScript();
    evertecInstance = P;
    evertecInstance.init(processUrl, { backupTarget: 'self' });
  };

  const openLightbox = async ({ processUrl, onApproved, onRejected, onClose }) => {
    if (!processUrl) {
      throw new Error('Missing processUrl for Evertec lightbox');
    }

    attachRedirectListener();
    clearLightboxCallbacks();
    const P = await loadScript();
    evertecInstance = P;
    lightboxCallbacks = { onApproved, onRejected, onClose };
    attachLightboxListeners(evertecInstance);
    evertecInstance.init(processUrl, { backupTarget: 'self' });
  };

  const getInstance = () => {
    return evertecInstance;
  };

  return {
    initialize,
    openLightbox,
    getInstance,
    createSession,
  };
};

const evertec = Evertec();

export default evertec;
